import React from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { StartTimeInterval } from 'common/types';
import moment from 'moment-timezone';
import { Box, TextField, FormHelperText } from '@mui/material';
import { useTranslation } from 'services/localization/useTranslation';

interface Props {
	value: StartTimeInterval;
	onChange: (value: StartTimeInterval) => void;
	disabled?: boolean;
}

const IntervalDuration = (props: Props) => {
	const { value, onChange } = props;
	const { intervalMinutes } = value;
	const hours = Math.floor(intervalMinutes / 60);
	const minutes = intervalMinutes % 60;
	const { t } = useTranslation();
	const classes = useStyles();

	const handleStartIntervalChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const { name } = e.target;
		const parsedValue = Number(e.target.value);
		const isSelectHours24 = name === 'hours' && parsedValue === 24;
		const isTotal0Mins = name === 'hours' && parsedValue === 0 && minutes === 0;
		const correctedMinutes = isSelectHours24 ? 0 : isTotal0Mins ? 5 : minutes;
		const newIntervalMinutes =
			name === 'hours' ? parsedValue * 60 + correctedMinutes : hours * 60 + parsedValue;
		onChange({
			...value,
			intervalMinutes: newIntervalMinutes,
		});
	};

	const getStartTimeSlotsExample = () => {
		const openTime = '9:00';
		const closeTime = '21:00';
		const [openHour, openMinute] = openTime.split(':').map((val) => Number(val));
		const [closeHour, closeMinute] = closeTime.split(':').map((val) => Number(val));
		const totalOpenMinutes = (closeHour - openHour) * 60 + (closeMinute - openMinute);
		const timeSlots = [0, 1, 2].reduce((slots: string[], i) => {
			if (intervalMinutes * i < totalOpenMinutes) {
				slots.push(
					moment()
						.hour(openHour)
						.minute(openMinute + intervalMinutes * i)
						.format('HH:mm'),
				);
			}
			return slots;
		}, []);
		return timeSlots.length > 1
			? t('startTimeSlotsMAny', {
					timeSlot: timeSlots.join(', '),
					defaultValue: 'E.g. with opening hours 9-21, the start time slots will be: {{timeSlot}}.',
			  })
			: t('startTimeSlotsOne', { timeSlot: timeSlots[0] });
	};

	const renderHoursDropdown = () => (
		<TextField
			name="hours"
			variant="standard"
			select
			className={classes.dropdown}
			value={hours}
			onChange={handleStartIntervalChange}
			SelectProps={{
				native: true,
			}}
			margin="none"
			type="number"
			disabled={props.disabled}
		>
			{Array(25)
				.fill('')
				.map((h, i) => (
					<option key={i} value={i}>
						{i}
						{' h'}
					</option>
				))}
		</TextField>
	);

	const renderMinutesDropdown = () => (
		<TextField
			name="minutes"
			variant="standard"
			select
			className={classes.dropdown}
			value={minutes}
			onChange={handleStartIntervalChange}
			SelectProps={{
				native: true,
			}}
			margin="none"
			type="number"
			disabled={props.disabled}
		>
			{Array(12)
				.fill('')
				.map((m, i) => 5 * i)
				.filter((min) => (hours === 0 ? min !== 0 : hours === 24 ? min === 0 : true))
				.map((min) => (
					<option key={min} value={min}>
						{min}
						{' min'}
					</option>
				))}
		</TextField>
	);

	return (
		<Box className={classes.alignBox}>
			<Box className={classes.intervalSelection}>
				{renderHoursDropdown()}
				{renderMinutesDropdown()}
			</Box>
			<FormHelperText>{getStartTimeSlotsExample()}</FormHelperText>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		spacingTop: {
			marginTop: theme.spacing(4),
		},
		dropdown: {
			width: 87,
			marginRight: theme.spacing(2),
		},
		intervalSelection: {
			display: 'flex',
		},
		alignBox: {
			marginLeft: 30,
		},
	}),
);

export default IntervalDuration;
